import {GraphQL} from "../graphql"
import {Form, Popup} from "semantic-ui-react"
import {errorUnionToEither} from "../graphql/errors"
import {BookingPaidBy, SetPaymentDetails} from "../graphql/queries"
import * as React from "react"
import {StripeCardField, StripeCardFieldState} from "./AddBookingWizard"
import {bookingCardHasExpired} from "../model/booking"
import {PaymentInviteField, PaymentInviteOuterState} from "./PaymentInviteField"
import {SaveResetButtons, SectionProps, UpdatableFormSection} from "./UpdateBookingWizard"
import {MapState} from "./BookingDetails"

interface PaymentDetailsSectionState {
  stripeCard: StripeCardFieldState
  paymentLink: PaymentInviteOuterState
  paidBy: BookingPaidBy
}

export function PaymentDetailsSection(props: SectionProps) {
  const {booker, booking} = props
  const {isAdmin} = booker
  const {paymentLink} = booking
  const isBooker = booking.booker && booking.booker.id === booker.id
  const state: PaymentDetailsSectionState = {
    stripeCard: {
      card: booking.stripeCard,
      loading: false,
    },
    paymentLink: {
      toEmail: paymentLink && paymentLink.toEmail,
      editing: false,
    },
    paidBy: booking.paidBy,
  }
  const canSetInvite = isBooker || isAdmin
  const showPaidBy = isAdmin && (booking.isComplete || booking.isCancelled)
  // If booking.booker is null admin cannot register a credit card as there is no one to own it.
  const payingBooker = isAdmin ? booking.booker : booker
  return (
    <UpdatableFormSection
      {...props}
      state={state}
      header="Payment Details"
      render={({state, mapState}) => {
        const enableSave =
          !state.paymentLink.editing &&
          !state.stripeCard.loading &&
          !bookingCardHasExpired(state.stripeCard.card, booking.bookingDate)
        return (
          <Form>
            {showPaidBy && (
              <PaidByField
                {...props}
                state={state.paidBy}
                mapState={
                  mapState &&
                  ((fn) => {
                    mapState((state) => {
                      return {...state, paidBy: fn(state.paidBy)}
                    })
                  })
                }
              />
            )}
            {payingBooker && (
              <StripeCardField
                {...props}
                state={state.stripeCard}
                mapState={
                  mapState &&
                  ((fn) => {
                    mapState((state) => {
                      return {...state, stripeCard: fn(state.stripeCard)}
                    })
                  })
                }
                payingBooker={payingBooker}
                additionalStripeCard={booking.stripeCard}
                bookingDate={booking.bookingDate}
              />
            )}
            {canSetInvite && (
              <PaymentInviteField
                {...props}
                state={state.paymentLink}
                mapState={
                  mapState && (isAdmin || isBooker)
                    ? (fn) => {
                        mapState((state) => {
                          return {...state, paymentLink: fn(state.paymentLink)}
                        })
                      }
                    : null
                }
              />
            )}
            {mapState && (
              <>
                <Form.Group>
                  <SaveResetButtons
                    {...props}
                    onSave={
                      enableSave
                        ? async () => {
                            const data = await props.modalStore.applyModal({
                              pending: ["Saving payment details", "Saving payment details.."],
                              error: (m) => ["Failed to save payment details", m],
                              action: async () => {
                                const {card} = state.stripeCard
                                const data = await GraphQL.mutate(SetPaymentDetails, {
                                  id: props.booking.id,
                                  stripeCardId: {
                                    uuid: card ? card.id : null,
                                  },
                                  // Only send up the paymentLinkToEmail if they actually have permission to set
                                  // the payment link (otherwise we might get a "not authorised" error response)
                                  paymentLinkToEmail: canSetInvite
                                    ? {
                                        string: state.paymentLink.toEmail,
                                      }
                                    : null,
                                  // Only send paidBy if they have permission to set it.
                                  paidBy: showPaidBy ? state.paidBy : null,
                                })
                                return errorUnionToEither(data.setPaymentDetails)
                              },
                            })
                            return data.isRight()
                          }
                        : null
                    }
                  />
                </Form.Group>
              </>
            )}
          </Form>
        )
      }}
    />
  )
}
interface PaidByFieldProps {
  disabled: boolean
  state: BookingPaidBy
  mapState: MapState<BookingPaidBy> | null
}

function PaidByField(props: PaidByFieldProps) {
  const {mapState, state, disabled} = props

  function paidByText(paidBy: BookingPaidBy): string {
    switch (paidBy) {
      case BookingPaidBy.not_specified:
        return "Not specified"
      case BookingPaidBy.on_account:
        return "On account"
      case BookingPaidBy.registered_credit_card:
        return "Registered CC"
      case BookingPaidBy.other:
        return "Other"
      case BookingPaidBy.paid_in_car_income:
        return "Paid in car - income"
      case BookingPaidBy.paid_in_car_non_income:
        return "Paid in car - non income"
    }
  }

  function paidByOptions() {
    return Object.values(BookingPaidBy).map((option) => ({
      key: option,
      text: paidByText(option),
      value: option,
    }))
  }

  // noinspection JSUnusedLocalSymbols
  return (
    <Form.Field>
      <Popup
        trigger={<label>Paid by</label>}
        content={`Remember to set the Booker "Invoice Terms" field if using "On account".`}
        wide
      />
      {mapState ? (
        <Form.Dropdown
          disabled={disabled}
          name="paidBy"
          selection
          search={false}
          onChange={(e, field) => mapState((state) => field.value as BookingPaidBy)}
          value={state}
          options={paidByOptions()}
        />
      ) : (
        <span>{paidByText(state)}</span>
      )}
    </Form.Field>
  )
}



// WEBPACK FOOTER //
// ./src/components/PaymentDetails.tsx